import { z } from 'zod'
import { ApiError } from '@/lib/api-error'
import { serviceRequest } from '@/lib/api-client'

const featureDecisionSchema = z.object({
  allowed: z.boolean(),
  action: z.literal('FEATURE'),
  applicationId: z.string(),
  resourceId: z.string().nullish(),
  effectiveAccess: z.enum(['NONE', 'READ', 'WRITE', 'MANAGE']),
  source: z.string(),
  permission: z.string(),
  reasonCode: z.string(),
  policyVersion: z.string(),
  policyRevision: z.string(),
  resourceVersion: z.number().int().nonnegative().nullish(),
  evaluatedAt: z.string(),
})

const envelopeSchema = z.object({
  status: z.literal('SUCCESS'),
  data: featureDecisionSchema,
})

const featureAccessSchema = z.object({
  status: z.literal('SUCCESS'),
  data: z.object({
    policyRevision: z.string(),
    applicationPermissions: z.record(z.string(), z.array(z.string())),
    fullAccessApplicationIds: z.array(z.string()).default([]),
  }),
})

export type FeatureResourceAccess = 'NONE' | 'READ' | 'WRITE'

export type FeatureDecision = z.infer<typeof featureDecisionSchema>

export type FeatureAccess = {
  policyRevision: string
  applicationPermissions: Record<string, string[]>
  fullAccessApplicationIds: string[]
}

export async function getFeatureAccess(): Promise<FeatureAccess> {
  const response = await serviceRequest<unknown>('onboarding', {
    method: 'GET',
    url: '/v2/api/authorization/features/access',
  })
  return featureAccessSchema.parse(response.data).data
}

export async function requireFeatureAccess(input: {
  applicationId: string
  permission: string
  resourceId?: string
  requiredResourceAccess: FeatureResourceAccess
}): Promise<FeatureDecision> {
  const response = await serviceRequest<unknown>('onboarding', {
    method: 'POST',
    url: '/v2/api/authorization/features/decision',
    data: input,
  })
  const decision = envelopeSchema.parse(response.data).data
  if (
    !decision.allowed ||
    decision.applicationId !== input.applicationId ||
    decision.permission !== input.permission ||
    (input.resourceId && decision.resourceId !== input.resourceId)
  ) {
    throw new ApiError({
      message: 'The requested feature operation is not authorized.',
      kind: 'authorization',
      status: 403,
      code: decision.reasonCode,
      retryable: false,
    })
  }
  return decision
}

export async function canUseFeature(
  input: Parameters<typeof requireFeatureAccess>[0],
): Promise<boolean> {
  try {
    await requireFeatureAccess(input)
    return true
  } catch (error) {
    const kind =
      error && typeof error === 'object' && 'kind' in error
        ? error.kind
        : null
    if (kind === 'authorization' || kind === 'not-found') {
      return false
    }
    throw error
  }
}
